import { useContext } from 'react';
import { TenantConfigContext } from 'context/TenantConfigContext';
import { DataRequest, useDataRequest } from 'hooks/useDataRequest';
import { DEFAULT_PRO_FILTER_SET, Service, useDataService, useProDataService } from 'services/data.service';
import { useQuery } from '@tanstack/react-query';
import { DataResult } from 'models/data';

export const useProductSummary = (productNumber: string, ignoreSalesDimensions = false) => {
  const { accountNumber } = useContext(TenantConfigContext);
  const requestData = useDataRequest();
  const { fetchData } = useProDataService(Service.Entities);

  // Fetch product attributes without filters so the summary
  // tile always shows product info (name, colors, sizes, etc.) even when the
  // filtered sales data returns no results
  const amendedDataRequest = {
    ...requestData,
    filters: {
      productNumbers: [productNumber],
    },
  };

  return useQuery({
    enabled: Boolean(productNumber),
    queryKey: [
      'product-summary',
      productNumber,
      ignoreSalesDimensions,
      accountNumber,
      requestData.filters,
      requestData.timeFilter,
      requestData.comparisonTimeFilter,
    ],
    queryFn: async (): Promise<DataResult | null> => {
      const response = await fetchData(`product-summary`, amendedDataRequest);
      if (response.error) {
        // Needed as our 'makeApiCall' is not returning standard error response but just an error attribute
        // This assures useQuery sees this as an error and exposes it via it's standard mechanism
        throw Error(response.error.message);
      }
      return response.data ?? null;
    },
    staleTime: 0,
  });
};

export const useStyleSummary = (productNumber: string, style: string) => {
  const { accountNumber } = useContext(TenantConfigContext);
  const requestData = useDataRequest();
  const { fetchData } = useProDataService(Service.Entities);

  const amendedDataRequest = {
    ...requestData,
    filters: {
      ...requestData.filters,
      productNumbers: [productNumber],
      colors: [style],
    },
  };

  return useQuery({
    enabled: Boolean(productNumber),
    queryKey: [
      'style-summary',
      productNumber,
      accountNumber,
      requestData.filters,
      requestData.timeFilter,
      requestData.comparisonTimeFilter,
    ],
    queryFn: async (): Promise<DataResult | null> => {
      const response = await fetchData(`style-summary`, amendedDataRequest);
      if (response.error) {
        // Needed as our 'makeApiCall' is not returning standard error response but just an error attribute
        // This assures useQuery sees this as an error and exposes it via it's standard mechanism
        throw Error(response.error.message);
      }
      return response.data ?? null;
    },
    staleTime: 0,
  });
};

export const useArticleSummary = (articleNumber: string) => {
  const { accountNumber } = useContext(TenantConfigContext);
  const requestData = useDataRequest();
  const { fetchData } = useProDataService(Service.Entities, { ...DEFAULT_PRO_FILTER_SET, articleEANs: true });

  const amendedDataRequest = {
    ...requestData,
    filters: {
      ...requestData.filters,
      articleEANs: [articleNumber],
    },
  };

  return useQuery({
    enabled: Boolean(articleNumber),
    queryKey: [
      'article-summary',
      articleNumber,
      accountNumber,
      requestData.filters,
      requestData.timeFilter,
      requestData.comparisonTimeFilter,
    ],
    queryFn: async (): Promise<DataResult | null> => {
      const response = await fetchData(`article-summary`, amendedDataRequest);
      if (response.error) {
        // Needed as our 'makeApiCall' is not returning standard error response but just an error attribute
        // This assures useQuery sees this as an error and exposes it via it's standard mechanism
        throw Error(response.error.message);
      }
      return response.data ?? null;
    },
    staleTime: 0,
  });
};

export const useCategoryProductCount = (category: string) => {
  const { accountNumber } = useContext(TenantConfigContext);
  const requestData = useDataRequest();
  const amendedDataRequest = {
    ...requestData,
    filters: {
      ...requestData.filters,
      categories: [category],
    },
  };

  const getData = useDataService(Service.Entities, DEFAULT_PRO_FILTER_SET).get<number>('data');

  return useQuery({
    enabled: Boolean(category),
    queryKey: [
      'category-product-count',
      category,
      accountNumber,
      requestData.filters,
      requestData.timeFilter,
      requestData.comparisonTimeFilter,
    ],
    queryFn: async (): Promise<number | null> => {
      const response = await getData('product-count', amendedDataRequest);
      if (response.error) {
        throw Error(response.error.message);
      }
      return response.data ?? null;
    },
    staleTime: 0,
  });
};

export const useBreakdownQuery = (requestData: DataRequest, attribute: string) => {
  const { accountNumber } = useContext(TenantConfigContext);
  const getData = useDataService(Service.Entities, DEFAULT_PRO_FILTER_SET).get<DataResult>(`breakdown-report`);

  return useQuery({
    queryKey: [
      'entity-breakdown',
      attribute,
      accountNumber,
      requestData.filters,
      requestData.timeFilter,
      requestData.comparisonTimeFilter,
    ],
    queryFn: async (): Promise<DataResult | null> => {
      const response = await getData(attribute, requestData);
      if (response.error) {
        throw Error(response.error.message);
      }
      return response.data ?? null;
    },
    staleTime: 0,
  });
};

export const useKpiData = (requestData: DataRequest) => {
  const { accountNumber } = useContext(TenantConfigContext);
  const { fetchData } = useProDataService(Service.Entities);

  const amendedDataRequest = {
    ...requestData,
  };

  return useQuery({
    queryKey: [
      'entity-kpi',
      accountNumber,
      requestData.filters,
      requestData.timeFilter,
      requestData.comparisonTimeFilter,
    ],
    queryFn: async (): Promise<DataResult | null> => {
      const response = await fetchData('dashboard', amendedDataRequest);
      if (response.error) {
        // Needed as our 'makeApiCall' is not returning standard error response but just an error attribute
        // This assures useQuery sees this as an error and exposes it via it's standard mechanism
        throw Error(response.error.message);
      }
      return response.data ?? null;
    },
    staleTime: 0,
  });
};

export const useTimeSeries = (requestData: DataRequest, measureCode: string) => {
  const { accountNumber } = useContext(TenantConfigContext);
  const { fetchData } = useProDataService(Service.Entities);

  const amendedDataRequest = {
    ...requestData,
  };

  return useQuery({
    enabled: Boolean(measureCode),
    queryKey: [
      'measure-timeseries',
      measureCode,
      accountNumber,
      requestData.filters,
      requestData.timeFilter,
      requestData.comparisonTimeFilter,
    ],
    queryFn: async (): Promise<DataResult | null> => {
      const response = await fetchData(`dashboard/time-series/${measureCode}`, amendedDataRequest);
      if (response.error) {
        // Needed as our 'makeApiCall' is not returning standard error response but just an error attribute
        // This assures useQuery sees this as an error and exposes it via it's standard mechanism
        throw Error(response.error.message);
      }
      return response.data ?? null;
    },
    staleTime: 0,
  });
};

export const usePriceKpiData = (requestData: DataRequest) => {
  const { accountNumber } = useContext(TenantConfigContext);
  const { fetchData } = useProDataService(Service.Price);

  return useQuery({
    queryKey: [
      'entity-price-kpis',
      accountNumber,
      requestData.filters,
      requestData.timeFilter,
      requestData.comparisonTimeFilter,
    ],
    queryFn: async (): Promise<DataResult | null> => {
      const response = await fetchData('kpis', requestData);
      if (response.error) {
        throw Error(response.error.message);
      }
      return response.data ?? null;
    },
    staleTime: 0,
  });
};

export const usePriceTimeSeries = (requestData: DataRequest) => {
  const { accountNumber } = useContext(TenantConfigContext);
  const { fetchData } = useProDataService(Service.Price);

  return useQuery({
    queryKey: [
      'entity-price-time-series',
      accountNumber,
      requestData.filters,
      requestData.timeFilter,
      requestData.comparisonTimeFilter,
    ],
    queryFn: async (): Promise<DataResult | null> => {
      const response = await fetchData('time-series', requestData);
      if (response.error) {
        throw Error(response.error.message);
      }
      return response.data ?? null;
    },
    staleTime: 0,
  });
};
